import React, { useId } from 'react';
import Dialog from '../../common/Dialog';
import type { FamilyPortalGuardianRow } from '../../../dtos/portal/familyPortalAdmin.dto';
import { GUARDIAN_NEVER_SEES, GUARDIAN_WILL_SEE } from './familyPortalText';

interface InviteDialogProps {
  open: boolean;
  guardian: FamilyPortalGuardianRow | null;
  busy: boolean;
  /** Resend replaces a pending or locked invite with a new code */
  mode: 'invite' | 'resend';
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirms an invite and shows exactly what the guardian will and will never see (FAM-06). */
const InviteDialog: React.FC<InviteDialogProps> = ({ open, guardian, busy, mode, onConfirm, onCancel }) => {
  const id = useId();
  if (!guardian) return null;
  return (
    <Dialog open={open} onClose={busy ? () => undefined : onCancel} labelledBy={`${id}-title`} describedBy={`${id}-desc`}>
      <div className="text-left">
        <h3 id={`${id}-title`} className="pr-6 text-lg font-semibold text-slate-800">
          {mode === 'resend' ? 'Resend family portal invite' : 'Invite to the family portal'}
        </h3>
        <p id={`${id}-desc`} className="mt-1 text-sm text-slate-700">
          {guardian.name} ({guardian.email}) will get an email with a secure link. It contains no agency, patient or health
          information. To accept, they must enter the patient’s last name and date of birth. After they set a password, you
          approve their access here.
          {mode === 'resend' && ' The previous link stops working.'}
        </p>
        <h4 className="mt-4 text-sm font-semibold text-slate-800">What the guardian will see</h4>
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-slate-700">
          {GUARDIAN_WILL_SEE.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <h4 className="mt-4 text-sm font-semibold text-slate-800">What they will never see</h4>
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-slate-700">
          {GUARDIAN_NEVER_SEES.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-slate-600">The portal is read-only. Guardians can’t change anything.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-md bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {busy ? 'Sending...' : mode === 'resend' ? 'Resend invite' : 'Send invite'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default InviteDialog;
